import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import CuttingOrderForm from "../../components/cuttingOrders/CuttingOrderForm";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import { cardClass, pageTitleClass } from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearCurrentCuttingOrder,
  clearCuttingOrderPartyInfo,
  fetchCuttingOrderDetail,
  fetchCuttingOrderMaterials,
  fetchCuttingOrderParties,
  fetchCuttingOrderPartyInfo,
  getCurrentCuttingOrder,
  getCurrentCuttingOrderLoading,
  getCuttingOrderMaterials,
  getCuttingOrderPartyInfo,
  getCuttingOrderPartyInfoLoading,
  getCuttingOrderPartyOptions,
} from "../../features/cuttingOrders/cuttingOrdersSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { PUT } from "../../services/axios.service";
import { orderLinePayload } from "../../static/cuttingLine";
import { withOrderBars } from "../../static/materialStock";
import { hasPermission, permission } from "../../static/permission";
import { rateText } from "../../static/processRate";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import {
  CuttingBarsPayload,
  CuttingMaterialInput,
  CuttingSkipPieceProps,
  ICuttingOrderFormInput,
} from "../../types/cuttingOrder";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "કટીંગ ઓર્ડર", href: route.cuttingOrders },
  { label: "ઓર્ડર સુધારો" },
];

const CuttingOrderEditPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const currentOrder = useAppSelector(getCurrentCuttingOrder);
  const currentOrderLoading = useAppSelector(getCurrentCuttingOrderLoading);
  const partyOptions = useAppSelector(getCuttingOrderPartyOptions);
  const materials = useAppSelector(getCuttingOrderMaterials);
  const partyInfo = useAppSelector(getCuttingOrderPartyInfo);
  const partyInfoLoading = useAppSelector(getCuttingOrderPartyInfoLoading);
  const loggingUser = useAppSelector(getLoggingUser);

  const withRates = hasPermission(
    loggingUser?.permissions,
    permission.cuttingRate
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const orderId = currentOrder?.id;
  const orderPartyId = currentOrder?.party_id;

  useEffect(() => {
    if (id) {
      dispatch(fetchCuttingOrderDetail(Number(id)));
      dispatch(fetchCuttingOrderMaterials());
    }
    return () => {
      dispatch(clearCurrentCuttingOrder());
      dispatch(clearCuttingOrderPartyInfo());
    };
  }, [id, dispatch]);

  // The order's own party stays in the list, and its own lines and bars don't count in what the party held before it.
  useEffect(() => {
    if (orderId && orderPartyId) {
      dispatch(fetchCuttingOrderParties(orderPartyId));
      dispatch(
        fetchCuttingOrderPartyInfo({ party_id: orderPartyId, order_id: orderId })
      );
    }
  }, [orderId, orderPartyId, dispatch]);

  const handlePartyChange = useCallback(
    (partyId: number) => {
      if (partyId) {
        dispatch(
          fetchCuttingOrderPartyInfo({ party_id: partyId, order_id: Number(id) })
        );
      } else {
        dispatch(clearCuttingOrderPartyInfo());
      }
    },
    [id, dispatch]
  );

  const handleSubmit = async (
    data: ICuttingOrderFormInput,
    sentBars: CuttingBarsPayload[],
    skipPieces: CuttingSkipPieceProps[]
  ) => {
    setIsSubmitting(true);
    try {
      await PUT(`/v1/admin/cutting-orders/${id}`, {
        party_id: data.party_id,
        date: moment(data.date).format("YYYY-MM-DD HH:mm:ss"),
        items: orderLinePayload(data.items, withRates),
        materials: sentBars,
        skip_pieces: skipPieces,
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
            navigate(route.cuttingOrders);
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultValues = useMemo((): ICuttingOrderFormInput | undefined => {
    if (!currentOrder) return undefined;
    return {
      party_id: currentOrder.party_id,
      date: moment(currentOrder.date).format("YYYY-MM-DDTHH:mm"),
      items: (currentOrder.items || []).map((item) => ({
        item_id: item.item_id,
        size_id: item.size_id,
        qty: String(item.qty),
        rate: rateText(item.rate),
        refresh: false,
      })),
    };
  }, [currentOrder]);

  // What the order did with each material: the bars sent (the first size of a material, as the form sends one size) and
  // the party's pieces it left alone.
  const defaultMaterials = useMemo(() => {
    const inputs: Record<number, CuttingMaterialInput> = {};
    const inputOf = (materialId: number) => {
      if (!inputs[materialId]) {
        inputs[materialId] = { skip: [], sizeId: null, qty: null };
      }

      return inputs[materialId];
    };

    (currentOrder?.materials || []).forEach((sent) => {
      const input = inputOf(sent.material_id);

      if (input.sizeId === null) {
        input.sizeId = sent.material_size_id;
        input.qty = String(sent.qty);
      }
    });

    (currentOrder?.skip_pieces || []).forEach((piece) =>
      inputOf(piece.material_id).skip.push(piece.length)
    );

    return inputs;
  }, [currentOrder]);

  // The stock already counts this order's bars as sent, so they are free again for it.
  const stockMaterials = useMemo(
    () => withOrderBars(materials, currentOrder),
    [materials, currentOrder]
  );

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>કટીંગ ઓર્ડર સુધારો</h1>
      </div>

      <div className="flex flex-col gap-6">
        {currentOrderLoading ? (
          <div className={cx(cardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : currentOrder && defaultValues ? (
          <CuttingOrderForm
            key={currentOrder.id}
            defaultValues={defaultValues}
            defaultMaterials={defaultMaterials}
            partyOptions={partyOptions}
            partyInfo={partyInfo}
            partyInfoLoading={partyInfoLoading}
            materials={stockMaterials}
            savedOrder={currentOrder}
            partyLocked={(currentOrder.receives || []).length > 0}
            onPartyChange={handlePartyChange}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onCancel={() => navigate(route.cuttingOrders)}
            submitLabel="સાચવો"
            withRates={withRates}
          />
        ) : (
          <div className={cx(cardClass, "p-8 text-center text-gray-500")}>
            કટીંગ ઓર્ડર ની વિગતો મળી નથી
          </div>
        )}
      </div>
    </AppLayout>
  );
};
export default CuttingOrderEditPage;
